import React from "react"
import ButtonBase, { ButtonBaseProps } from "@mui/material/ButtonBase"
import { SxProps, Theme as MuiTheme } from "@mui/material/styles"
import { useConfig } from "../../hooks/useConfig"
import * as ColorUtil from "../../utils/ColorUtil"

interface BigButtonProps extends Omit<ButtonBaseProps, "sx"> {
  variant?: "filled" | "outlined" | "text"
  size?: "default" | "small"
  fullWidth?: boolean
  sx?: SxProps<MuiTheme>
}

export default function BigButton({ variant = "filled", size = "default", fullWidth = false, sx, children, ...props }: BigButtonProps) {
  const theme = useConfig().theme

  const variantStyles =
    variant === "text"
      ? {
          color: theme.textColor,
        }
      : variant === "outlined"
        ? {
            color: theme.textColor,
            border: "3px solid " + theme.textColor,
          }
        : {
            color: theme.highestContrastBackgroundColor,
            backgroundColor: theme.textColor,
            boxShadow: "0px 5px 0px " + ColorUtil.hexToCssRgba(theme.textColor, 0.7),
          }

  const widthStyles = fullWidth
    ? { width: "100%" }
    : {
        minWidth: { xs: "auto", sm: 280 },
        "@media (min-width: 620px)": {
          maxWidth: 435,
        },
      }

  return (
    <ButtonBase
      {...props}
      sx={[
        {
          paddingLeft: "20px",
          paddingRight: "20px",
          height: size === "small" ? 50 : 75,
          borderRadius: "9px",
          fontSize: size === "small" ? 16 : 21,
          fontWeight: size === "small" ? 500 : 600,
          letterSpacing: 0,
          lineHeight: "25px",
          userSelect: "none",
          outline: "none",
          transition: "transform 0.2s",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          "&:hover": {
            transform: "scale(1.05)",
          },
          "&:active": {
            opacity: 0.6,
          },
          "&.Mui-disabled": {
            opacity: 0.35,
            pointerEvents: "none",
          },
          ...widthStyles,
          ...variantStyles,
        },
        ...(Array.isArray(sx) ? sx : sx ? [sx] : []),
      ]}
    >
      {children}
    </ButtonBase>
  )
}
